import type { BramxCompareResponse, ComparedPlayer, EntrySummary } from '../lib/api';
import { previewModelSignalContract } from '../lib/model-contract';
import { playerMatchDisplayState, type PlayerMatchDisplayState, type TeamMatchDisplayState } from '../lib/live-player-state';

interface Props {
  side: 'A' | 'B';
  nickname: string;
  entry: EntrySummary | null;
  players: ComparedPlayer[];
  simulation: BramxCompareResponse['simulation'] | null;
  preview?: boolean;
  picksBasisGameweek?: number | null;
  basis?: 'PUBLISHED_TARGET_GW' | 'LAST_PUBLISHED_SQUAD' | 'PLANNED' | null;
  liveMatchByTeam?: ReadonlyMap<number, TeamMatchDisplayState>;
}

export function SquadPanel({ side, nickname, entry, players, simulation, preview = false, picksBasisGameweek = null, basis = null, liveMatchByTeam }: Props) {
  const multiplierKey = side === 'A' ? 'multiplierA' : 'multiplierB';
  const positionKey = side === 'A' ? 'positionA' : 'positionB';
  const owned = players
    .filter((player) => player[multiplierKey] > 0 || player[positionKey] !== null)
    .sort((a, b) => (a[positionKey] ?? 99) - (b[positionKey] ?? 99));
  const starters = owned.filter((player) => (player[positionKey] ?? 99) <= 11);
  const bench = owned.filter((player) => (player[positionKey] ?? 0) > 11);
  const simSide = side === 'A' ? simulation?.gameweek.a : simulation?.gameweek.b;
  const complete = !preview && Boolean(entry && entry.expectedRemaining === 0 && owned.length > 0 && owned.every((player) => player.fixtureStatus === 'FINISHED' || player.fixtureStatus === 'NO_FIXTURE'));

  return (
    <section className="panel squad-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="eyebrow">SQUAD DUEL · {side}</div>
          <h2 className="mt-1 text-xl font-black tracking-tight text-white">{entry?.teamName || nickname}</h2>
          <p className="mt-0.5 text-xs text-slate-500">{entry?.managerName || 'Public FPL entry'}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="badge-official">{preview ? basis === 'PLANNED' ? 'PLANNED · BRAM' : `LAST-PUBLISHED SCENARIO · GW${picksBasisGameweek ?? '—'}` : 'OFFICIAL + LIVE'}</span>
          {simulation && <span className="badge-sim">{complete ? 'GW COMPLETE' : `${simulation.label} · ${simulation.iterations.toLocaleString('nl-NL')}`}</span>}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-2 xl:grid-cols-4">
        <Metric label={preview ? 'Base' : 'Live'} value={entry ? fmt(entry.provisionalPointsFromPublishedPicks) : '—'} />
        <Metric label={preview ? 'GW EV' : 'Remain'} value={entry ? `+${fmt(entry.expectedRemaining)}` : '—'} />
        <Metric label={preview ? 'Preview' : 'Proj'} value={entry ? fmt(entry.projectedFinalPoints) : '—'} />
        <Metric label="Win" value={simSide ? `${simSide.winProbability.toFixed(1)}%` : '—'} accent />
      </div>

      {simSide && (
        <div className="mt-3 rounded-xl border border-cyan-400/10 bg-cyan-400/[0.035] px-3 py-2 text-[11px] text-slate-500">
          {complete ? <>FINAL SCORE <span className="font-bold text-white">{fmt(simSide.mean)}</span> · uncertainty = 0</> : preview ? <>NEXT-GW PREVIEW · {basis === 'PLANNED' ? 'planned Bram state' : 'last-published scenario'} · P10–P90 <span className="font-bold text-slate-300">{fmt(simSide.p10)}–{fmt(simSide.p90)}</span> · mean <span className="font-bold text-white">{fmt(simSide.mean)}</span></> : <>SIM range P10–P90 <span className="font-bold text-slate-300">{fmt(simSide.p10)}–{fmt(simSide.p90)}</span> · mean <span className="font-bold text-white">{fmt(simSide.mean)}</span></>}
        </div>
      )}

      <Roster title="Starting XI" players={starters} multiplierKey={multiplierKey} preview={preview} liveMatchByTeam={liveMatchByTeam} />
      <Roster title="Bench" players={bench} multiplierKey={multiplierKey} compact preview={preview} liveMatchByTeam={liveMatchByTeam} />
    </section>
  );
}

function Roster({ title, players, multiplierKey, compact = false, preview = false, liveMatchByTeam }: { title: string; players: ComparedPlayer[]; multiplierKey: 'multiplierA' | 'multiplierB'; compact?: boolean; preview?: boolean; liveMatchByTeam?: ReadonlyMap<number, TeamMatchDisplayState> }) {
  return (
    <div className="mt-5 border-t border-slate-800/90 pt-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">{title}</span>
        <span className="text-[10px] text-slate-700">{players.length}</span>
      </div>
      <div className={compact ? 'space-y-1.5' : 'space-y-2'}>
        {players.map((player) => {
          const multiplier = player[multiplierKey];
          const captain = multiplierKey === 'multiplierA' ? player.captainA : player.captainB;
          const matchDisplay = playerMatchDisplayState(player, liveMatchByTeam?.get(player.teamId), preview);
          const statusTone = matchDisplay.tone === 'live' ? 'text-emerald-300' : matchDisplay.tone === 'finished' ? 'text-slate-500' : matchDisplay.tone === 'upcoming' ? 'text-amber-300' : 'text-slate-600';
          const modelContract = previewModelSignalContract(player.modelSignals);
          return (
            <div key={player.elementId} className="player-row">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-[13px] font-extrabold text-slate-200">{player.webName}</span>
                  {captain && <span className="captain-chip">C×{Math.max(2, multiplier)}</span>}
                </div>
                <div className="mt-0.5 flex gap-2 text-[10px] text-slate-600">
                  <span>{player.teamShortName} · {positionLabel(player.elementType)}</span>
                  <span className={statusTone} title={`Resolved display source: ${matchDisplay.sourceProvider}`}>{matchDisplay.label}</span>
                  <span>FPL {player.minutesPlayed}m</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-right tabular-nums">
                <TinyStat label={preview ? 'BASE' : 'FPL'} value={fmt(player.livePoints * multiplier)} />
                <TinyStat label={preview ? 'GW EV' : 'REM'} value={`+${fmt(player.expectedRemaining * multiplier)}`} />
                <TinyStat label={preview ? 'PREV' : 'PROJ'} value={fmt((player.livePoints + player.expectedRemaining) * multiplier)} bright />
              </div>
              {preview ? (
                <div className="col-span-full mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[9px] uppercase tracking-wide text-slate-700">
                  <span className="font-black text-cyan-300">BRAM-X CORE {signal(modelContract.core)}</span>
                  <span>FPL BENCHMARK {signal(modelContract.benchmark)}</span>
                  <span>DELTA {signalNullableSigned(modelContract.delta)}</span>
                  <span className="text-amber-300">CALIBRATION {modelContract.calibrationStatus}</span>
                  <span>EP BENCH {signal(player.modelSignals.benchmarkEp)}</span>
                  <span>FORM {signal(player.form)}</span>
                  <span>xGI90 {signal(player.xgiPer90)}</span>
                  <span>xMIN {player.minuteDistribution.expectedMinutes.toFixed(0)}</span>
                  <span>PSTART {(player.minuteDistribution.pStart * 100).toFixed(0)}%</span>
                  <span>{roleBadge(player.minuteDistribution.pStart, player.minuteDistribution.pCameo)}</span>
                  <span>TX {signedCompact(player.transferNetEvent)}</span>
                  <span>OWN {player.selectedByPercent === null ? '—' : `${player.selectedByPercent.toFixed(1)}%`}</span>
                  <span>DC {player.dcState.prematchRate90 === null ? '—' : player.dcState.prematchRate90.toFixed(1)+'/90'}</span>
                  <span>SRC {player.projectionSources.slice(0, 2).join(' + ')}</span>
                  <span className="ml-auto">D{player.confidence.dataCompleteness} · I{player.confidence.informationConfidence} · M{player.confidence.modelConfidence}</span>
                </div>
              ) : (
                <div className="col-span-full mt-1 flex gap-3 text-[9px] uppercase tracking-wide text-slate-700">
                  <span>{liveStartLabel(player, matchDisplay)}</span>
                  <span>BPS {player.bps}</span>
                  <span>DC {player.dcState.currentCount}/{player.dcState.threshold}{player.dcState.pThreshold === null ? '' : ` · P+2 ${(player.dcState.pThreshold * 100).toFixed(0)}%`}</span>
                  <span>G {player.goals}</span>
                  <span>A {player.assists}</span>
                  {player.liveXg !== null && <span>xG {player.liveXg.toFixed(2)}</span>}
                  {player.liveXa !== null && <span>xA {player.liveXa.toFixed(2)}</span>}
                  {player.liveXgi !== null && <span>xGI {player.liveXgi.toFixed(2)}</span>}
                  <span>PRE Pstart {(player.minuteDistribution.pStart * 100).toFixed(0)}% · P60 {(player.minuteDistribution.p60Plus * 100).toFixed(0)}% · P75 {(player.minuteDistribution.p75Plus * 100).toFixed(0)}% · P90 {(player.minuteDistribution.p90 * 100).toFixed(0)}% · xMin {player.minuteDistribution.expectedMinutes.toFixed(0)}</span>
                  <span>ROLE {player.roleState.status === 'MODEL-EST' ? player.roleState.roleLabel : 'PENDING'}</span>
                  <span className={matchDisplay.tone !== 'upcoming' && player.actualStarted !== null && Math.abs((player.actualStarted ? 1 : 0) - player.minuteDistribution.pStart) > 0.45 ? 'text-amber-300' : ''}>WATCH {liveWatch(player, matchDisplay)}</span>
                  <span className="ml-auto">MODEL-EST · D{player.confidence.dataCompleteness}/I{player.confidence.informationConfidence}/M{player.confidence.modelConfidence}</span>
                </div>
              )}
            </div>
          );
        })}
        {players.length === 0 && <p className="py-2 text-xs text-slate-600">Waiting for published picks.</p>}
      </div>
    </div>
  );
}

function Metric({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className={`metric-card ${accent ? 'metric-accent' : ''}`}>
      <div className="metric-label">{label}</div>
      <div className="metric-value">{value}</div>
    </div>
  );
}

function TinyStat({ label, value, bright = false }: { label: string; value: string; bright?: boolean }) {
  return (
    <div>
      <div className="text-[8px] font-bold uppercase tracking-[0.12em] text-slate-700">{label}</div>
      <div className={`text-[12px] font-black ${bright ? 'text-cyan-200' : 'text-slate-300'}`}>{value}</div>
    </div>
  );
}

function fmt(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function signal(value: number | null): string { return value === null ? '—' : value.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1'); }
function signalNullableSigned(value: number | null): string { if (value === null) return '—'; return `${value >= 0 ? '+' : ''}${signal(value)}`; }
function signedCompact(value: number): string { const abs = Math.abs(value); const base = abs >= 1_000_000 ? `${(abs / 1_000_000).toFixed(1)}m` : abs >= 1_000 ? `${(abs / 1_000).toFixed(1)}k` : String(abs); return `${value > 0 ? '+' : value < 0 ? '-' : ''}${base}`; }

function roleBadge(pStart: number, pCameo: number): string { if (pStart >= 0.78) return 'ROLE START'; if (pStart >= 0.48) return 'ROLE ROTATION'; if (pCameo >= 0.25) return 'ROLE CAMEO'; return 'ROLE RISK'; }

function liveStartLabel(player: ComparedPlayer, match: PlayerMatchDisplayState): string {
  if (match.tone === 'upcoming') return 'LINEUP PENDING';
  if (player.actualStarted === true) return 'STARTED';
  if (player.actualStarted === false) return 'BENCH/NO START';
  if (player.minutesPlayed > 0) return 'APPEARED';
  return 'START ?';
}

function liveWatch(player: ComparedPlayer, match: PlayerMatchDisplayState): string {
  if (match.tone === 'upcoming') return 'WAIT FOR LINEUP';
  if (player.actualStarted === false && player.minuteDistribution.pStart >= 0.65) return 'START MISS → FUTURE EVIDENCE';
  if (player.actualStarted === true && player.minuteDistribution.pStart <= 0.45) return 'START UPSIDE → FUTURE EVIDENCE';
  if (player.fixtureStatus === 'FINISHED' && Math.abs(player.minutesPlayed - player.minuteDistribution.expectedMinutes) >= 20) return 'MINUTES GAP → FUTURE EVIDENCE';
  return 'STABLE';
}

function positionLabel(elementType: number): string { return elementType === 1 ? 'GK' : elementType === 2 ? 'DEF' : elementType === 3 ? 'MID' : elementType === 4 ? 'FWD' : '?'; }
